import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface SmoothScrollOptions {
  offset?: number;
  duration?: number;
}

export const useSmoothScroll = (options: SmoothScrollOptions = {}) => {
  const { offset = 80 } = options;
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToElement = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const targetPosition = elementPosition - offset;

    // Use native smooth scroll with easing
    window.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });
  }, [offset]);

  const handleAnchorClick = useCallback((
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    onComplete?: () => void
  ) => {
    if (!href.startsWith("#")) return;
    
    e.preventDefault();
    const targetId = href.replace("#", "");

    // If not on homepage, navigate first
    if (location.pathname !== "/") {
      navigate("/" + href);
      onComplete?.();
      return;
    }

    scrollToElement(targetId);
    onComplete?.();
  }, [location.pathname, navigate, scrollToElement]);

  return { scrollToElement, handleAnchorClick };
};

// Luxury easing curves for premium animations
export const luxuryEasing = {
  smooth: [0.25, 0.1, 0.25, 1] as const,
  elegant: [0.6, 0.01, 0.05, 0.95] as const,
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
  swift: [0.4, 0, 0.2, 1] as const,
};

// Premium page transition variants
export const pageTransitionVariants = {
  initial: {
    opacity: 0,
    y: 30,
    scale: 0.98,
    filter: "blur(4px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.6,
      ease: luxuryEasing.elegant,
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.99,
    filter: "blur(2px)",
    transition: {
      duration: 0.4,
      ease: luxuryEasing.swift,
    },
  },
};

// Section reveal variants for scroll animations
export const sectionRevealVariants = {
  hidden: {
    opacity: 0,
    y: 60,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: luxuryEasing.elegant,
    },
  },
};
